import {
  applePay,
  ApplePay,
  client,
  Client,
  GooglePayment,
  googlePayment,
} from 'braintree-web';
import { config } from 'src/config';
import { FunnelController } from 'src/controllers/business/FunnelController';
import { IBuyButtonIds } from 'src/controllers/forms/CheckoutFormController';
import { CheckoutController } from 'src/controllers/pages/CheckoutController';
import { IProduct } from 'src/types/product';

interface MerchantValidationEvent {
  validationURL: string;
}

interface AuthorizePayment {
  payment: {
    token: string;
    shippingContact: any;
  };
}

export class BriantreeService {
  private static instance: Client;
  private static gPayClient: google.payments.api.PaymentsClient;
  private static gPay: GooglePayment;
  private static applePay: ApplePay;
  private static gPayAvailable: boolean = false;
  private static appleAvailable: boolean = false;
  private fc: FunnelController;
  private buttonIds: IBuyButtonIds;

  private static checkAvailability = async () => {
    try {
      BriantreeService.appleAvailable = !!(
        window?.ApplePaySession &&
        window.ApplePaySession?.supportsVersion(3) &&
        window.ApplePaySession?.canMakePayments()
      );

      BriantreeService.gPayClient = new google.payments.api.PaymentsClient({
        environment: window.DEBUG ? 'TEST' : 'PRODUCTION',
      });
      const res = await BriantreeService.gPayClient.isReadyToPay({
        apiVersion: 2,
        apiVersionMinor: 0,
        existingPaymentMethodRequired: true,
        allowedPaymentMethods: (
          await BriantreeService.gPay.createPaymentDataRequest()
        ).allowedPaymentMethods,
      });
      BriantreeService.gPayAvailable = !!res.result;
    } catch (e) {
      Errors.report(e, undefined, false, false);
    }
  };

  static init = async (fc: FunnelController, buttonIds: IBuyButtonIds) => {
    if (BriantreeService.instance) {
      return new BriantreeService(fc, buttonIds);
    }
    BriantreeService.instance = await client.create({
      authorization: config.BR_KEY,
    });
    BriantreeService.gPay = await googlePayment.create({
      googlePayVersion: 2,
      client: BriantreeService.instance,
      googleMerchantId: config.BR_ID,
    });
    BriantreeService.applePay = await applePay.create({
      client: BriantreeService.instance,
    });
    await BriantreeService.checkAvailability();
    return new BriantreeService(fc, buttonIds);
  };

  private constructor(fc: FunnelController, buttonIds: IBuyButtonIds) {
    this.fc = fc;
    this.buttonIds = buttonIds;
  }

  maybeRenderButtons = () => {
    const { applePayEnabled, googlePayEnabled } = this.fc.config;

    const appleButtonCont = document.getElementById('applePayContainer');
    if (appleButtonCont) {
      appleButtonCont.style.display =
        applePayEnabled && BriantreeService.appleAvailable ? 'flex' : 'none';
    }
    const gpayButtonCont = document.getElementById('googlePayContainer');
    if (gpayButtonCont) {
      gpayButtonCont.style.display =
        googlePayEnabled && BriantreeService.gPayAvailable ? 'flex' : 'none';
    }
  };

  createGPayOrder = async (product: IProduct) => {
    try {
      const price = (product.finalPrice + product.shippingPrice).toFixed(2);
      const paymentRequest =
        await BriantreeService.gPay.createPaymentDataRequest({
          transactionInfo: {
            currencyCode: product.currencyCode,
            totalPriceStatus: 'FINAL',
            totalPrice: price,
          },
        });
      const paymentResponse = await BriantreeService.gPayClient.loadPaymentData(
        paymentRequest,
      );
      return await BriantreeService.gPay.parseResponse(paymentResponse);
    } catch (e) {
      Errors.report(
        e,
        {
          product,
        },
        false,
        false,
      );
    }
  };

  private createApplePaymentRequest = (
    countryCode: string,
    product: IProduct,
    expressButton = false,
  ) => {
    const addons = this.fc.getSelectedAddons();
    let totalAmount = product?.finalPrice + product.shippingPrice;
    if (addons && Array.isArray(addons)) {
      addons.forEach(addon => {
        totalAmount += addon.finalPrice;
      });
    }
    let subTotalAmount = product.finalPrice;
    if (addons && Array.isArray(addons)) {
      addons.forEach(addon => {
        subTotalAmount += addon.finalPrice;
      });
    }
    const shippingPrice = product.shippingPrice;

    const contactFields = expressButton
      ? ['email', 'phone', 'name', 'postalAddress']
      : [];

    return BriantreeService.applePay.createPaymentRequest({
      countryCode,
      currencyCode: product.currencyCode,
      total: {
        label: window.BRAND.toUpperCase(),
        amount: totalAmount?.toFixed(2),
      },
      requiredShippingContactFields: contactFields,
      lineItems: [
        {
          label: 'Subtotal',
          amount: subTotalAmount?.toFixed(2),
        },
        {
          label: 'Shipping',
          amount: shippingPrice?.toFixed(2),
        },
      ],
    });
  };

  createAppleSession = async (
    checkout: CheckoutController,
    expressButton = false,
  ) => {
    const product = this.fc.getSelectedProduct();
    const country = this.fc.lead?.lead.country || window.GEO.country;
    const pr = this.createApplePaymentRequest(country, product, expressButton);
    const session = new window.ApplePaySession(3, pr);

    session.onvalidatemerchant = async (ev: MerchantValidationEvent) => {
      try {
        const data = await BriantreeService.applePay.performValidation({
          validationURL: ev.validationURL,
          displayName: window.BRAND.toUpperCase(),
          merchantIdentifier: config.BR_ID,
        });

        session.completeMerchantValidation(data);
      } catch (e) {
        Errors.report(
          e,
          {
            product,
            geo: window.GEO,
            config: this.fc.config,
            lead: this.fc.lead?.lead,
          },
          false,
          false,
        );
      }
    };

    session.onpaymentauthorized = async (ev: AuthorizePayment) => {
      const data = await BriantreeService.applePay.tokenize({
        token: ev.payment.token,
      });

      if (expressButton) {
        await checkout.createApplePayOrderExpress(
          data.nonce,
          session,
          ev.payment.shippingContact,
        );
      } else {
        await checkout.createApplePayOrder(data.nonce, session);
      }
    };

    return session;
  };
}
